import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster, toast } from "./components/ui/sonner";
import { ThemeProvider } from "next-themes";
import { I18nProvider, useI18n } from "./contexts/I18nContext";
import { lazy, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import { LoginPage } from "./pages/LoginPage";
import { AuthCallback } from "./pages/AuthCallback";
import { DashboardLayout } from "./components/DashboardLayout";
import { SystemSubpageGuard } from "./components/SystemSubpageGuard";
import { buildLocaleRootPath, buildServerPath, getPreferredLang, normalizeLang } from "./lib/routes";
import { buildDashboardAccessContext, canAccessRoute } from "./lib/dashboardAccess";
import { startPerformanceNavigation } from "./lib/performanceTrace";
import { AccessibleGuildsProvider, useAccessibleGuilds } from "./contexts/AccessibleGuildsContext";
import { dispatchGuildSelection } from "./lib/guildSelection";

const lazyNamed = (loader, exportName) =>
  lazy(() => loader().then((module) => ({ default: module[exportName] })));

const OverviewPage = lazyNamed(
  () => import(/* webpackChunkName: "page-xp-overview" */ "./pages/OverviewPage"),
  "OverviewPage",
);
const XPSettingsPage = lazyNamed(
  () => import(/* webpackChunkName: "page-xp-settings" */ "./pages/XPSettingsPage"),
  "XPSettingsPage",
);
const LevelsPage = lazyNamed(
  () => import(/* webpackChunkName: "page-levels" */ "./pages/LevelsPage"),
  "LevelsPage",
);
const RankPage = lazyNamed(
  () => import(/* webpackChunkName: "page-rank" */ "./pages/RankPage"),
  "RankPage",
);
const SeasonsPage = lazyNamed(
  () => import(/* webpackChunkName: "page-seasons" */ "./pages/SeasonsPage"),
  "SeasonsPage",
);
const BonusXPPage = lazyNamed(
  () => import(/* webpackChunkName: "page-bonus-xp" */ "./pages/BonusXPPage"),
  "BonusXPPage",
);
const ExclusionsPage = lazyNamed(
  () => import(/* webpackChunkName: "page-exclusions" */ "./pages/ExclusionsPage"),
  "ExclusionsPage",
);
const NotificationsPage = lazyNamed(
  () => import(/* webpackChunkName: "page-notifications" */ "./pages/NotificationsPage"),
  "NotificationsPage",
);
const AdminRolesPage = lazyNamed(
  () => import(/* webpackChunkName: "page-admin-roles" */ "./pages/AdminRolesPage"),
  "AdminRolesPage",
);
const LeaderboardPage = lazyNamed(
  () => import(/* webpackChunkName: "page-leaderboard" */ "./pages/LeaderboardPage"),
  "LeaderboardPage",
);
const UsersPage = lazyNamed(
  () => import(/* webpackChunkName: "page-users" */ "./pages/UsersPage"),
  "UsersPage",
);
const LogsPage = lazyNamed(
  () => import(/* webpackChunkName: "page-logs" */ "./pages/LogsPage"),
  "LogsPage",
);
const ServerOverviewPage = lazyNamed(
  () => import(/* webpackChunkName: "page-server-overview" */ "./pages/ServerOverviewPage"),
  "ServerOverviewPage",
);
const MessagesPage = lazyNamed(
  () => import(/* webpackChunkName: "page-messages" */ "./pages/MessagesPage"),
  "MessagesPage",
);
const ServerSettingsPage = lazyNamed(
  () => import(/* webpackChunkName: "page-server-settings" */ "./pages/ServerSettingsPage"),
  "ServerSettingsPage",
);
const LevelSystemPage = lazyNamed(
  () => import(/* webpackChunkName: "page-level-system" */ "./pages/LevelSystemPage"),
  "LevelSystemPage",
);
const LogsSystemPage = lazyNamed(
  () => import(/* webpackChunkName: "page-logs-system" */ "./pages/LogsSystemPage"),
  "LogsSystemPage",
);
const UserProfilePage = lazyNamed(
  () => import(/* webpackChunkName: "page-user-profile" */ "./pages/UserProfilePage"),
  "UserProfilePage",
);
const WelcomeSystemPage = lazyNamed(
  () => import(/* webpackChunkName: "page-welcome-system" */ "./pages/WelcomeSystemPage"),
  "WelcomeSystemPage",
);
const AutoModerationPage = lazyNamed(
  () => import(/* webpackChunkName: "page-automod-system" */ "./pages/AutoModerationPage"),
  "AutoModerationPage",
);
const AutoModerationRulesPage = lazyNamed(
  () => import(/* webpackChunkName: "page-automod-rules" */ "./pages/AutoModerationRulesPage"),
  "AutoModerationRulesPage",
);
const AutoModerationViolationPointsPage = lazyNamed(
  () => import(/* webpackChunkName: "page-automod-violations" */ "./pages/AutoModerationViolationPointsPage"),
  "AutoModerationViolationPointsPage",
);
const CommandsSystemPage = lazyNamed(
  () => import(/* webpackChunkName: "page-commands-system" */ "./pages/CommandsSystemPage"),
  "CommandsSystemPage",
);
const AutoRolesSystemPage = lazyNamed(
  () => import(/* webpackChunkName: "page-auto-roles-system" */ "./pages/AutoRolesSystemPage"),
  "AutoRolesSystemPage",
);
const TempVoicePage = lazyNamed(
  () => import(/* webpackChunkName: "page-temp-voice" */ "./pages/TempVoicePage"),
  "TempVoicePage",
);

const readStoredArray = (key) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const syncGuildSelection = (guildId, guild = null) => {
  const gid = String(guildId || "").trim();
  if (!gid) return;
  const previousAccess = readStoredArray("tns:selected_guild_access");
  const previousScopes = readStoredArray("tns:selected_guild_scopes");
  const nextAccess = Array.isArray(guild?.access_type) ? guild.access_type : previousAccess;
  const nextScopes = Array.isArray(guild?.dashboard_scopes) ? guild.dashboard_scopes : previousScopes;
  const nextGuild = {
    id: gid,
    name: guild?.name || "",
    icon: guild?.icon || "",
    access_type: nextAccess,
    dashboard_scopes: nextScopes,
    bot_in_guild: guild?.bot_in_guild,
  };
  localStorage.setItem("tns:selected_guild_id", gid);
  localStorage.setItem("tns:selected_guild_access", JSON.stringify(nextGuild.access_type));
  localStorage.setItem("tns:selected_guild_scopes", JSON.stringify(nextGuild.dashboard_scopes));
  dispatchGuildSelection(nextGuild);
};

const clearGuildSelection = () => {
  localStorage.removeItem("tns:selected_guild_id");
  localStorage.removeItem("tns:selected_guild_access");
  localStorage.removeItem("tns:selected_guild_scopes");
  dispatchGuildSelection(null);
};

const PerformanceNavigationTracker = () => {
  const location = useLocation();

  useEffect(() => {
    startPerformanceNavigation(`${location.pathname}${location.search || ""}`);
  }, [location.pathname, location.search]);

  return null;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={buildLocaleRootPath(getPreferredLang())} replace />;
  }

  return children;
};

const LocaleRoute = ({ children }) => {
  const { lang } = useParams();
  const location = useLocation();
  const normalized = normalizeLang(lang);

  if (lang !== normalized) {
    const rest = location.pathname.split("/").slice(2).join("/");
    const suffix = rest ? `/${rest}` : "";
    return <Navigate to={`/${normalized}${suffix}${location.search || ""}${location.hash || ""}`} replace />;
  }

  return children;
};

const AccountScopeRoute = ({ children }) => {
  useEffect(() => {
    clearGuildSelection();
  }, []);
  return children;
};

const ServerScopeRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const {
    guilds,
    loading: guildsLoading,
    error: guildsError,
  } = useAccessibleGuilds();
  const { t } = useI18n();
  const { guildId } = useParams();
  const [hasAccess, setHasAccess] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const gid = String(guildId || "").trim();
    if (!gid) {
      setHasAccess(false);
      setChecking(false);
      return;
    }
    if (loading || guildsLoading) {
      setChecking(true);
      return;
    }

    const matchedGuild = guildsError
      ? null
      : (Array.isArray(guilds) ? guilds : []).find(
          (guild) => String(guild?.id || "") === gid,
        );
    setHasAccess(Boolean(matchedGuild));
    setChecking(false);
    if (matchedGuild) syncGuildSelection(gid, matchedGuild);
  }, [guildId, guilds, guildsError, guildsLoading, loading, user?.user_id]);

  if (loading || checking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={buildLocaleRootPath(getPreferredLang())} replace />;
  }

  if (hasAccess === false) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-heading font-bold text-destructive">{t("ممنوع الوصول", "Access Denied")}</h1>
          <p className="text-muted-foreground">
            {t(
              "ليس لديك صلاحية لهذا الخادم. يجب أن تكون مالك الخادم أو تملك صلاحية Administrator.",
              "You don't have permission for this server. You must be the server owner or have Administrator permission.",
            )}
          </p>
        </div>
      </div>
    );
  }

  return children;
};

const ScopedRouteGuard = ({ children, requiredScope = "", ownerOnly = false }) => {
  const { t } = useI18n();
  const location = useLocation();
  const { lang, guildId } = useParams();
  const deniedPathRef = useRef("");

  const accessContext = buildDashboardAccessContext({
    access_type: readStoredArray("tns:selected_guild_access"),
    dashboard_scopes: readStoredArray("tns:selected_guild_scopes"),
  });

  const allowed = canAccessRoute(accessContext, {
    requiredScope,
    ownerOnly,
  });

  useEffect(() => {
    if (allowed) return;
    if (deniedPathRef.current === location.pathname) return;
    deniedPathRef.current = location.pathname;
    toast.error(
      t(
        "لا تملك صلاحية الوصول لهذه الصفحة.",
        "You do not have permission to access this page.",
      ),
    );
  }, [allowed, location.pathname, t]);

  if (allowed) return children;

  return (
    <Navigate
      to={buildServerPath(normalizeLang(lang), guildId, "server-overview")}
      replace
    />
  );
};

function AppRoutes() {
  const preferredLang = getPreferredLang();

  return (
    <Routes>
      <Route path="/" element={<Navigate to={buildLocaleRootPath(preferredLang)} replace />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      <Route
        path="/:lang"
        element={
          <LocaleRoute>
            <LoginPage />
          </LocaleRoute>
        }
      />

      <Route
        path="/:lang/dashboard"
        element={
          <ProtectedRoute>
            <LocaleRoute>
              <AccountScopeRoute>
                <DashboardLayout />
              </AccountScopeRoute>
            </LocaleRoute>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="profile" replace />} />
        <Route path="profile" element={<UserProfilePage sectionOverride="profile" />} />
        <Route path="subscription" element={<UserProfilePage sectionOverride="subscription" />} />
        <Route path="servers" element={<UserProfilePage sectionOverride="servers" />} />
        <Route path="billing" element={<UserProfilePage sectionOverride="billing" />} />
        <Route path="support" element={<UserProfilePage sectionOverride="support" />} />
        <Route path="*" element={<Navigate to="profile" replace />} />
      </Route>

      <Route
        path="/:lang/server/:guildId"
        element={
          <ProtectedRoute>
            <LocaleRoute>
              <ServerScopeRoute>
                <DashboardLayout />
              </ServerScopeRoute>
            </LocaleRoute>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="server-overview" replace />} />
        <Route path="server-overview" element={<ServerOverviewPage />} />
        <Route path="xp-overview" element={<OverviewPage />} />
        <Route path="systems/level" element={<ScopedRouteGuard requiredScope="level_system"><LevelSystemPage /></ScopedRouteGuard>} />
        <Route path="systems/logs" element={<ScopedRouteGuard requiredScope="logs_system"><LogsSystemPage /></ScopedRouteGuard>} />
        <Route path="systems/welcome" element={<ScopedRouteGuard requiredScope="welcome_system"><WelcomeSystemPage /></ScopedRouteGuard>} />
        <Route path="systems/automod" element={<ScopedRouteGuard requiredScope="automod_system"><AutoModerationPage /></ScopedRouteGuard>} />
        <Route path="systems/commands" element={<Navigate to="general" replace />} />
        <Route path="systems/commands/general" element={<ScopedRouteGuard requiredScope="commands_system"><CommandsSystemPage mode="general" /></ScopedRouteGuard>} />
        <Route path="systems/commands/moderation" element={<ScopedRouteGuard requiredScope="commands_system"><CommandsSystemPage mode="moderation" /></ScopedRouteGuard>} />
        <Route path="systems/auto-roles" element={<ScopedRouteGuard requiredScope="commands_system"><AutoRolesSystemPage /></ScopedRouteGuard>} />
        <Route path="systems/temp-voice" element={<ScopedRouteGuard requiredScope="temp_voice_system"><TempVoicePage /></ScopedRouteGuard>} />
        <Route path="systems/automod/moderation" element={<ScopedRouteGuard requiredScope="automod_system"><SystemSubpageGuard systemKey="automod"><AutoModerationRulesPage /></SystemSubpageGuard></ScopedRouteGuard>} />
        <Route path="systems/automod/violations" element={<ScopedRouteGuard requiredScope="automod_system"><SystemSubpageGuard systemKey="automod"><AutoModerationViolationPointsPage /></SystemSubpageGuard></ScopedRouteGuard>} />
        <Route path="systems/welcome/join" element={<ScopedRouteGuard requiredScope="welcome_system"><SystemSubpageGuard systemKey="welcome"><WelcomeSystemPage /></SystemSubpageGuard></ScopedRouteGuard>} />
        <Route path="systems/welcome/image" element={<Navigate to="../join" replace />} />
        <Route path="systems/welcome/leave" element={<ScopedRouteGuard requiredScope="welcome_system"><SystemSubpageGuard systemKey="welcome"><WelcomeSystemPage /></SystemSubpageGuard></ScopedRouteGuard>} />
        <Route path="xp-settings" element={<ScopedRouteGuard requiredScope="level_system"><SystemSubpageGuard systemKey="level"><XPSettingsPage /></SystemSubpageGuard></ScopedRouteGuard>} />
        <Route path="levels" element={<ScopedRouteGuard requiredScope="level_system"><SystemSubpageGuard systemKey="level"><LevelsPage /></SystemSubpageGuard></ScopedRouteGuard>} />
        <Route path="rank" element={<ScopedRouteGuard requiredScope="level_system"><SystemSubpageGuard systemKey="level"><RankPage /></SystemSubpageGuard></ScopedRouteGuard>} />
        <Route path="seasons" element={<ScopedRouteGuard requiredScope="level_system"><SystemSubpageGuard systemKey="level"><SeasonsPage /></SystemSubpageGuard></ScopedRouteGuard>} />
        <Route path="bonus-xp" element={<ScopedRouteGuard requiredScope="level_system"><SystemSubpageGuard systemKey="level"><BonusXPPage /></SystemSubpageGuard></ScopedRouteGuard>} />
        <Route path="xp-events" element={<Navigate to="../bonus-xp" relative="path" replace />} />
        <Route path="exclusions" element={<ScopedRouteGuard requiredScope="level_system"><SystemSubpageGuard systemKey="level"><ExclusionsPage /></SystemSubpageGuard></ScopedRouteGuard>} />
        <Route path="notifications" element={<ScopedRouteGuard requiredScope="level_system"><SystemSubpageGuard systemKey="level"><NotificationsPage /></SystemSubpageGuard></ScopedRouteGuard>} />
        <Route path="admin-roles" element={<ScopedRouteGuard ownerOnly><AdminRolesPage /></ScopedRouteGuard>} />
        <Route path="logs" element={<ScopedRouteGuard requiredScope="logs_system"><SystemSubpageGuard systemKey="logs"><LogsPage /></SystemSubpageGuard></ScopedRouteGuard>} />
        <Route path="embeds" element={<ScopedRouteGuard requiredScope="embeds_system"><MessagesPage /></ScopedRouteGuard>} />
        <Route path="server-settings" element={<ScopedRouteGuard ownerOnly><ServerSettingsPage /></ScopedRouteGuard>} />
        <Route path="leaderboard" element={<LeaderboardPage />} />
        <Route path="users" element={<ScopedRouteGuard requiredScope="level_system"><SystemSubpageGuard systemKey="level"><UsersPage /></SystemSubpageGuard></ScopedRouteGuard>} />
        <Route path="*" element={<Navigate to="server-overview" replace />} />
      </Route>

      <Route path="*" element={<Navigate to={buildLocaleRootPath(preferredLang)} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AccessibleGuildsProvider>
        <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
          <BrowserRouter>
            <I18nProvider>
              <PerformanceNavigationTracker />
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </I18nProvider>
          </BrowserRouter>
        </ThemeProvider>
      </AccessibleGuildsProvider>
    </AuthProvider>
  );
}

export default App;
